// Re-export constants
export { gradientColors, tokens } from './constants.js';

// Re-export angle utilities
export {
  convertOrientationToRadians,
  convertAngleToRadians,
  prepareCssAngle,
  getRectAngleBound,
} from './angle-utils.js';

// Re-export color utilities
export {
  colorStopToString,
  calculateLinearStop,
  calculateRadialStop,
  addColorStops,
  fixTransparentColorStops,
} from './color-utils.js';

// Re-export parser
export {
  parse,
  parseUncached,
  GradientParserCore,
  parseCache,
  getParseStats,
  clearParseCache,
} from './parser.js';

// Re-export stringify
export { stringify } from './stringify.js';

// Re-export renderers
export {
  renderGradient,
  renderLinearGradient,
  renderRadialGradient,
  renderConicGradient,
  renderRepeatingLinearGradient,
  renderRepeatingRadialGradient,
  renderRepeatingConicGradient,
  getRenderStats,
  clearRenderStats,
} from './renderers/index.js';

// Import for GradientParser object construction
import { parse, getParseStats, clearParseCache } from './parser.js';
import { stringify } from './stringify.js';
import { prepareCssAngle, getRectAngleBound } from './angle-utils.js';
import { renderGradient, getRenderStats, clearRenderStats } from './renderers/index.js';

/**
 * Check if a fill value is a gradient
 * @param {string} fill - Fill value to check
 * @returns {boolean} True if the fill is a gradient
 */
export function isGradient(fill) {
  if (!fill || typeof fill !== 'string') return false;
  const normalizedFill = fill.trim().toLowerCase();
  return (
    normalizedFill.startsWith('linear-gradient') ||
    normalizedFill.startsWith('radial-gradient') ||
    normalizedFill.startsWith('conic-gradient') ||
    normalizedFill.startsWith('repeating-linear-gradient') ||
    normalizedFill.startsWith('repeating-radial-gradient') ||
    normalizedFill.startsWith('repeating-conic-gradient')
  );
}

/**
 * Create a canvas gradient from a CSS gradient string
 * @param {CanvasRenderingContext2D} context - Canvas context
 * @param {string} fill - CSS gradient string
 * @param {Object} size - Bounding box { x, y, w, h }
 * @param {Function} [render] - Optional render callback
 * @returns {CanvasGradient|null} Canvas gradient or null
 */
export function createGradient(context, fill, size, render) {
  try {
    const colors = parse(fill);
    // Filter out 'none' entries from multi-layer backgrounds
    const validColors = colors.filter((color) => color.type !== 'none');
    let pattern = null;
    validColors.reverse().forEach((color) => {
      pattern = renderGradient(context, color, size, render);
    });
    return pattern;
  } catch (e) {
    console.error(e, fill);
  }
  return null;
}

/**
 * Create a gradient fill if the color is a gradient, otherwise return the color
 * @param {CanvasRenderingContext2D} context - Canvas context
 * @param {string} color - Color or gradient string
 * @param {Object} bounds - Bounding box { x, y, w, h }
 * @returns {CanvasGradient|string} Canvas gradient or color string
 */
export function createGradientFill(context, color, bounds) {
  if (isGradient(color)) {
    return createGradient(context, color, bounds);
  }
  return color;
}

/**
 * GradientParser object for backward compatibility
 * Provides the same API as the original implementation
 */
export const GradientParser = {
  parse,
  stringify,
  prepareCssAngle,
  getRectAngleBound,
  create: createGradient,
  is: isGradient,

  // Cache management
  getStats: () => ({
    parse: getParseStats(),
    render: getRenderStats(),
  }),
  clearCache: () => {
    clearParseCache();
    clearRenderStats();
  },
};
